import React from "react"
import Button from "@mui/material/Button"
import { alpha } from "@mui/system"
import {
    BUTTON_FONT_FAMILY,
    BUTTON_FONT_WEIGHT,
    BUTTON_LETTER_SPACING,
    FONT_SIZE_2XS,
} from "../../style/fontSettings"
import { SECONDARY_HIGHLIGHT, MELOGY_TRANSPARENT, MELOGY_WHITE } from "../../style/colors"
import {
    getButtonColor,
    getButtonHoverColor,
    getButtonActiveColor,
} from "../../util/ButtonUtil"
import styled from "styled-components"

const StyledText = styled.div`
    width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: ${({ textAlign }) => textAlign || "left"};
    padding-left: 2px;
    padding-top: 4px;
`

const MelogyButton = ({
    text,
    height,
    width,
    onClick,
    justifyContent,
    fontSize,
    fontFamily,
    fontWeight,
    letterSpacing,
    textTransform,
    isSelected,
    buttonColor,
    icon,
    margin,
    isToggleSidebarButton,
    textAlign,
    borderBottom,
    transparentBackground,
}) => {
    const color = getButtonColor(buttonColor, isSelected)
    const colorHover = getButtonHoverColor(buttonColor)
    const colorActive = getButtonActiveColor(buttonColor)
    const [isTouched, setIsTouched] = React.useState(false)

    const mobileColor = isTouched ? colorActive : color
    const mobileBackgroundColor = transparentBackground
        ? (isTouched ? "rgba(0, 0, 0, 0.7)" : "transparent")
        : (isTouched 
            ? (text ? "rgba(0, 0, 0, 0.9)" : alpha(colorHover, 0.3))
            : (text ? "rgba(0, 0, 0, 0.9)" : "rgba(0, 0, 0, 0.9)"))

    return (
        <Button
            onClick={onClick}
            onTouchStart={() => setIsTouched(true)}
            onTouchEnd={() => setIsTouched(false)}
            startIcon={icon ? icon : null}
            sx={{
                fontFamily: fontFamily || BUTTON_FONT_FAMILY,
                fontSize: fontSize || FONT_SIZE_2XS,
                letterSpacing: letterSpacing || BUTTON_LETTER_SPACING,
                fontWeight: fontWeight || BUTTON_FONT_WEIGHT,
                justifyContent: justifyContent,
                width: width,
                minWidth: width,
                height: height,
                minHeight: height,
                color: color,
                backgroundColor: transparentBackground 
                    ? "transparent" 
                    : text 
                        ? "rgba(0, 0, 0, 0.9)" // Semi-transparent dark background for text buttons
                        : isSelected 
                            ? alpha(colorHover, 0.2) 
                            : "rgba(0, 0, 0, 0.9)", // Semi-transparent dark background for icon buttons
                backdropFilter: transparentBackground ? "none" : "blur(6px)", // Blur effect for better readability
                WebkitBackdropFilter: transparentBackground ? "none" : "blur(6px)", // Safari support
                padding: 0,
                margin: margin || 0,
                borderRadius: transparentBackground ? 0 : (text || isToggleSidebarButton ? 0 : "50%"),
                borderBottom: borderBottom ? borderBottom :
                    isSelected && text
                        ? `2px solid ${MELOGY_WHITE}`
                        : `2px solid ${MELOGY_TRANSPARENT}`,
                textTransform: textTransform || "none",
                transition: "color 0.3s ease, background-color 0.3s ease",

                "& .MuiButton-startIcon": {
                    margin: "auto",
                    width: "32px",
                    minWidth: "32px",
                    height: "32px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                },

                // Desktop (non-mobile) styles
                "&:hover": transparentBackground
                    ? {
                          color: colorHover,
                          backgroundColor: "rgba(0, 0, 0, 0.7)", // Subtle hover on transparent
                      }
                    : text
                        ? {
                              color: colorHover,
                              backgroundColor: "rgba(0, 0, 0, 0.9)", // Same on hover
                          }
                        : {
                              color: color, // maintain original color on hover
                              backgroundColor: isSelected
                                  ? alpha(colorHover, 0.3)
                                  : "rgba(0, 0, 0, 0.9)", // Same on hover
                          },

                // Mobile styles
                "@media (hover: none)": {
                    color: mobileColor,
                    backgroundColor:
                        isSelected && !text
                            ? alpha(colorHover, 0.2)
                            : mobileBackgroundColor,
                    "&:hover": {
                        color: mobileColor, // Restore original color
                        backgroundColor: mobileBackgroundColor,
                    },
                },

                "&:focus": {
                    outline: "none",
                },
            }}
        >
            {text && <StyledText textAlign={textAlign}>{text}</StyledText>}
        </Button>
    )
}

export default MelogyButton
